import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Share2, Youtube, ChevronUp, ChevronDown, Eye, Music2 } from "lucide-react";
import { listVideos, formatViews } from "@/lib/api";

const Shorts = () => {
  const [shorts, setShorts] = useState([]);
  const [active, setActive] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    listVideos({ kind: "shorts", sort: "popular", limit: 50 })
      .then(setShorts)
      .catch(() => setShorts([]));
  }, []);

  // Track active via IntersectionObserver
  useEffect(() => {
    if (!shorts.length) return;
    const cards = containerRef.current?.querySelectorAll("[data-short-idx]");
    if (!cards) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number(visible.target.getAttribute("data-short-idx")));
      },
      { root: containerRef.current, threshold: [0.5, 0.75] }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [shorts]);

  const goTo = (idx) => {
    const next = Math.max(0, Math.min(shorts.length - 1, idx));
    const card = containerRef.current?.querySelector(`[data-short-idx="${next}"]`);
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!shorts.length) {
    return (
      <div className="max-w-md mx-auto py-24 text-center" data-testid="shorts-empty">
        <Music2 className="w-10 h-10 mx-auto text-[var(--text-muted)] mb-3" />
        <p className="text-[var(--text-dim)]">A carregar shorts…</p>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="shorts-page">
      <div className="text-center pt-2 pb-1">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--bg-elevated)] border border-[var(--border)] text-xs">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot"></span>
          Feed contínuo · desliza para o próximo
        </span>
      </div>

      <div
        ref={containerRef}
        className="snap-y snap-mandatory overflow-y-auto scrollbar-hide"
        style={{ height: "calc(100vh - 130px)" }}
        data-testid="shorts-container"
      >
        {shorts.map((v, i) => (
          <ShortSlide
            key={v.id}
            video={v}
            isActive={i === active}
            index={i}
            onUp={() => goTo(i - 1)}
            onDown={() => goTo(i + 1)}
            showUp={i > 0}
            showDown={i < shorts.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

const ShortSlide = ({ video, isActive, index, onUp, onDown, showUp, showDown }) => {
  return (
    <section
      data-short-idx={index}
      data-testid={`short-slide-${video.id}`}
      className="snap-start flex items-center justify-center gap-4 px-4"
      style={{ height: "calc(100vh - 130px)" }}
    >
      {/* Player */}
      <div className="relative h-full max-h-[80vh] aspect-short rounded-2xl overflow-hidden bg-black border border-[var(--border)] shadow-2xl">
        {isActive ? (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&mute=0&loop=1&playlist=${video.id}&rel=0&modestbranding=1&hl=pt&playsinline=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid={`short-iframe-${video.id}`}
          />
        ) : (
          <img src={video.thumbnail_hq} alt={video.title} className="absolute inset-0 w-full h-full object-cover" onError={(e)=>{e.currentTarget.src=video.thumbnail;}} />
        )}

        {/* Overlay info */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/95 via-black/40 to-transparent">
          <h3 className="font-display text-lg sm:text-xl font-semibold text-white clamp-2 leading-snug">
            {video.title}
          </h3>
          <div className="flex items-center gap-2 mt-2 text-xs text-white/80">
            <Eye className="w-3 h-3" /> {formatViews(video.views)}
            <span>·</span>
            <span>Abel Andrade</span>
          </div>
        </div>
      </div>

      {/* Side actions */}
      <div className="hidden sm:flex flex-col items-center gap-4">
        {showUp ? (
          <button onClick={onUp} className="w-12 h-12 rounded-full bg-[var(--bg-elevated)] border border-[var(--border)] hover:bg-[var(--bg-hover)] flex items-center justify-center" data-testid={`short-up-${index}`}>
            <ChevronUp className="w-5 h-5" />
          </button>
        ) : <div className="w-12 h-12" />}

        <ActionButton icon={<Heart className="w-5 h-5" />} label={video.rating_count || 0} />
        <ActionButton
          icon={<Share2 className="w-5 h-5" />}
          label="Partilhar"
          onClick={async () => {
            try {
              const url = `${window.location.origin}/watch/${video.id}`;
              if (navigator.share) await navigator.share({ title: video.title, url });
              else await navigator.clipboard.writeText(url);
            } catch (err) {
              console.error("Share failed:", err);
            }
          }}
          testid={`short-share-${index}`}
        />
        <Link to={`/watch/${video.id}`} className="w-12 h-12 rounded-full bg-[var(--bg-elevated)] border border-[var(--border)] hover:bg-[var(--bg-hover)] flex items-center justify-center" data-testid={`short-detail-${index}`}>
          <Music2 className="w-5 h-5" />
        </Link>
        <a href={`https://www.youtube.com/shorts/${video.id}`} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white flex items-center justify-center transition-colors" data-testid={`short-yt-${index}`}>
          <Youtube className="w-5 h-5" />
        </a>

        {showDown ? (
          <button onClick={onDown} className="w-12 h-12 rounded-full bg-[var(--bg-elevated)] border border-[var(--border)] hover:bg-[var(--bg-hover)] flex items-center justify-center" data-testid={`short-down-${index}`}>
            <ChevronDown className="w-5 h-5" />
          </button>
        ) : <div className="w-12 h-12" />}
      </div>
    </section>
  );
};

const ActionButton = ({ icon, label, onClick, testid }) => (
  <button onClick={onClick} className="flex flex-col items-center gap-1 group" data-testid={testid}>
    <span className="w-12 h-12 rounded-full bg-[var(--bg-elevated)] border border-[var(--border)] flex items-center justify-center group-hover:bg-[var(--bg-hover)] transition-colors">
      {icon}
    </span>
    <span className="text-[10px] text-[var(--text-muted)]">{label}</span>
  </button>
);

export default Shorts;
